import React, { useEffect, useState } from "react";
import { supabase, fmtBRL } from "@/lib/supabase";
import { db } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Users, Briefcase, FileText, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import AnaliseComparativa from "./AnaliseComparativa";

const Metric = ({ label, value, sub, Icon, accent, testid, delta }) =>
<Card className="p-6 bg-white border border-black/10 rounded-none flex flex-col justify-between" data-testid={testid}>
    <div>
      <div className="flex items-start justify-between mb-4">
        <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A]">{label}</div>
        <Icon className={`w-4 h-4 ${accent ? "text-[#D9381E]" : "text-[#8A8A8A]"}`} />
      </div>
      <div className="font-serif text-4xl">{value}</div>
      {sub && <div className="text-sm text-[#4A4A4A] mt-1">{sub}</div>}
    </div>
    {delta &&
  <div className={`text-xs mt-4 flex items-center gap-1 font-mono ${delta.isPositive ? 'text-green-600' : delta.isNegative ? 'text-red-600' : 'text-[#8A8A8A]'}`}>
        {delta.isPositive ? '' : delta.isNegative ? '' : ''} {delta.text}
      </div>
  }
  </Card>;


export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().substring(0, 7));
  const [compareMonth, setCompareMonth] = useState("none");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [leadsData, projetosData, financeiroData, propostasData] = await Promise.all([
        db.select("leads").catch(() => []),
        db.select("projetos").catch(() => []),
        db.select("financeiro").catch(() => []),
        db.select("propostas").catch(() => [])]
        );

        setData({
          leads: Array.isArray(leadsData) ? leadsData : [],
          projetos: Array.isArray(projetosData) ? projetosData : [],
          financeiro: Array.isArray(financeiroData) ? financeiroData : [],
          propostas: Array.isArray(propostasData) ? propostasData : []
        });
      } catch (err) {
        console.error("Erro dashboard:", err);
      }
    })();
  }, [user]);

  const m = React.useMemo(() => {
    if (!data) return null;
    const { leads, projetos, financeiro, propostas } = data;

    const [year, month] = currentMonth.split("-");
    const monthKey = `${year}-${month}`;
    const [compYear, compMonth] = compareMonth !== "none" ? compareMonth.split("-") : [null, null];
    const compMonthKey = compareMonth !== "none" ? `${compYear}-${compMonth}` : null;

    const entradasPagas = financeiro.filter((f) => f && f.tipo === "entrada" && f.status === "pago");
    const totalReceita = entradasPagas.reduce((s, f) => s + Number(f.valor || 0), 0);

    const entradasMes = entradasPagas.filter((f) => {
      const dateStr = f.data || f.created_at || "";
      return dateStr.startsWith(monthKey);
    });
    const receitaMes = entradasMes.reduce((s, f) => s + Number(f.valor || 0), 0);

    const saidasPagas = financeiro.filter((f) => f && f.tipo === "saida" && f.status === "pago");
    const totalSaida = saidasPagas.reduce((s, f) => s + Number(f.valor || 0), 0);

    const saidasMes = saidasPagas.filter((f) => {
      const dateStr = f.data || f.created_at || "";
      return dateStr.startsWith(monthKey);
    });
    const despesaMes = saidasMes.reduce((s, f) => s + Number(f.valor || 0), 0);

    let receitaComp = 0;
    let despesaComp = 0;
    if (compMonthKey) {
      const entradasComp = entradasPagas.filter((f) => (f.data || f.created_at || "").startsWith(compMonthKey));
      receitaComp = entradasComp.reduce((s, f) => s + Number(f.valor || 0), 0);
      const saidasComp = saidasPagas.filter((f) => (f.data || f.created_at || "").startsWith(compMonthKey));
      despesaComp = saidasComp.reduce((s, f) => s + Number(f.valor || 0), 0);
    }

    const getDelta = (current, comp) => {
      if (compareMonth === "none") return null;
      if (comp === 0) return { text: current > 0 ? '100%' : '0%', isPositive: current > 0, isNegative: false };
      const diff = current - comp;
      const perc = diff / comp * 100;
      return {
        text: `${Math.abs(perc).toFixed(1)}%`,
        isPositive: diff > 0,
        isNegative: diff < 0
      };
    };

    const deltaReceita = getDelta(receitaMes, receitaComp);
    const deltaDespesa = getDelta(despesaMes, despesaComp);

    const aReceber = financeiro.
    filter((f) => f && f.tipo === "entrada" && f.status === "pendente").
    reduce((s, f) => s + Number(f.valor || 0), 0);

    const aPagar = financeiro.
    filter((f) => f && f.tipo === "saida" && f.status === "pendente").
    reduce((s, f) => s + Number(f.valor || 0), 0);

    const ticketMedio = entradasMes.length ? receitaMes / entradasMes.length : entradasPagas.length ? totalReceita / entradasPagas.length : 0;

    const fechados = leads.filter((l) => l && l.status === "fechado").length;
    const leadsAbertos = leads.filter((l) => l && !["fechado", "perdido"].includes(l.status)).length;
    const conversao = leads.length ? fechados / leads.length * 100 : 0;
    const projetosAtivos = projetos.filter((p) => p && p.status === "em andamento").length;

    const chart = [];
    const baseDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    for (let i = 11; i >= 0; i--) {
      const d = new Date(baseDate.getFullYear(), baseDate.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
      const total = entradasPagas.
      filter((f) => {
        const dateStr = f.data || f.created_at || "";
        return dateStr.startsWith(key);
      }).
      reduce((s, f) => s + Number(f.valor || 0), 0);
      const totalDespesa = saidasPagas.
      filter((f) => {
        const dateStr = f.data || f.created_at || "";
        return dateStr.startsWith(key);
      }).
      reduce((s, f) => s + Number(f.valor || 0), 0);
      chart.push({ month: label, revenue: Math.round(total * 100) / 100, expense: Math.round(totalDespesa * 100) / 100 });
    }

    const chartDataEntradas = (() => {
      const acc = {};
      entradasMes.forEach((i) => {
        const cat = i.categoria || i.servico || i.descricao || 'Outros';
        if (!acc[cat]) acc[cat] = 0;
        acc[cat] += Number(i.valor) || 0;
      });
      return Object.keys(acc).map((name) => ({ name, valor: acc[name] })).sort((a, b) => b.valor - a.valor).slice(0, 5);
    })();

    const chartDataSaidas = (() => {
      const acc = {};
      saidasMes.forEach((i) => {
        const cat = i.categoria || i.servico || i.descricao || 'Outros';
        if (!acc[cat]) acc[cat] = 0;
        acc[cat] += Number(i.valor) || 0;
      });
      return Object.keys(acc).map((name) => ({ name, valor: acc[name] })).sort((a, b) => b.valor - a.valor).slice(0, 5);
    })();

    const balancoMes = receitaMes - despesaMes;

    return {
      receitaMes, totalReceita, despesaMes, totalSaida, ticketMedio, aReceber, aPagar, leadsAbertos,
      conversao: Math.round(conversao * 10) / 10, projetosAtivos,
      totalPropostas: propostas.length, totalLeads: leads.length, chart,
      chartDataEntradas, chartDataSaidas, deltaReceita, deltaDespesa, balancoMes
    };
  }, [data, currentMonth]);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Painel</div>
          <h1 className="font-serif text-4xl md:text-5xl">Olá, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-[#4A4A4A] mt-2">Aqui estí sua revisãoo em números.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-tighter text-[#8A8A8A] mb-1">MÊSs</div>
            <Select value={currentMonth} onValueChange={setCurrentMonth}>
              <SelectTrigger className="rounded-none w-48 bg-white border-black/10 h-14">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 60 }).map((_, i) => {
                  const d = new Date();
                  d.setDate(1);
                  d.setMonth(d.getMonth() - i);
                  const val = d.toISOString().substring(0, 7);
                  const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                  return <SelectItem key={val} value={val} className="capitalize">{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-tighter text-[#8A8A8A] mb-1">Comparar com</div>
            <Select value={compareMonth} onValueChange={setCompareMonth}>
              <SelectTrigger className="rounded-none w-48 bg-white border-black/10 h-14">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum</SelectItem>
                {Array.from({ length: 60 }).map((_, i) => {
                  const d = new Date();
                  d.setDate(1);
                  d.setMonth(d.getMonth() - i);
                  const val = d.toISOString().substring(0, 7);
                  const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                  return <SelectItem key={val} value={val} className="capitalize">{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          {user?.plan &&
          <div className="bg-[#F6F5F0] border border-black/10 px-4 flex items-center justify-between gap-6 h-14">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-tighter text-[#8A8A8A]">Plano Atual</div>
                <div className="font-serif text-lg capitalize leading-none mt-1">{user.plan}</div>
              </div>
              <a
              href="https://www.mercadopago.com.br/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono uppercase tracking-widest text-[#D9381E] hover:underline">
              
                Gerenciar
              </a>
            </div>
          }
        </div>
      </div>

      {!m ?
      <div className="text-[#8A8A8A] font-mono text-sm">Carregando mêtricas&</div> :

      <>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <Metric label="Faturamento do mês" value={fmtBRL(m.receitaMes)} sub={`Total: ${fmtBRL(m.totalReceita)}`} Icon={TrendingUp} accent testid="metric-monthly-revenue" delta={m.deltaReceita} />
            <Metric label="Saídas do mês" value={fmtBRL(m.despesaMes)} sub={`Total: ${fmtBRL(m.totalSaida)}`} Icon={TrendingDown} testid="metric-monthly-expenses" delta={m.deltaDespesa} />
            <Metric label="Balanço do mês" value={fmtBRL(m.balancoMes)} Icon={Wallet} testid="metric-monthly-balance" />
            <Metric label="A receber" value={fmtBRL(m.aReceber)} Icon={Wallet} testid="metric-pending" />
            <Metric label="A pagar" value={fmtBRL(m.aPagar)} Icon={Wallet} testid="metric-pending-expenses" />
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <Metric label="Ticket mêdio" value={fmtBRL(m.ticketMedio)} Icon={Wallet} testid="metric-avg-ticket" />
            <Metric label="Taxa de conversão" value={`${m.conversao}%`} sub={`${m.leadsAbertos} leads abertos`} Icon={Users} testid="metric-conversion" />
            <Metric label="Projetos ativos" value={m.projetosAtivos} Icon={Briefcase} testid="metric-projects" />
            <Metric label="Propostas criadas" value={m.totalPropostas} Icon={FileText} testid="metric-proposals" />
            <Metric label="Leads" value={m.totalLeads} Icon={Users} testid="metric-leads" />
          </div>
          <Card className="p-6 bg-white border border-black/10 rounded-none">
            <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Faturamento e Saídas (Últimos 12 meses)</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={m.chart}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#E5E3DB" />
                  <XAxis dataKey="month" stroke="#4A4A4A" fontSize={12} />
                  <YAxis stroke="#4A4A4A" fontSize={12} />
                  <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ fontFamily: "Manrope", borderRadius: 0 }} />
                  <Legend wrapperStyle={{ fontSize: "12px", color: "#4A4A4A" }} />
                  <Bar dataKey="revenue" name="Faturamento" fill="#D9381E" />
                  <Bar dataKey="expense" name="Saídas" fill="#8A8A8A" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <div className="mt-16">
            <AnaliseComparativa />
          </div>
        </>
      }
    </div>);

}